import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Briefcase, ArrowRight, LogOut, FolderOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BusinessSelector = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('portal_token');
    if (!token) {
      navigate('/portal');
      return;
    }
    loadBusinesses();
  }, [navigate]);

  const loadBusinesses = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(`${API_URL}/api/businesses/my-businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = response.data;
      setData(result);
      
      // If only one business, auto-redirect
      if (result.count === 1) {
        selectBusiness(result.businesses[0]);
      }
    } catch (error) {
      console.error('Error loading businesses:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.clear();
        navigate('/portal');
      } else {
        toast.error('Error al cargar negocios');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectBusiness = (business) => {
    // Store selected business in localStorage
    localStorage.setItem('selected_business', JSON.stringify(business));
    navigate('/portal/dashboard');
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/portal');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#e03c3c] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1D1D1F]/80 font-semibold">Cargando...</p>
        </div>
      </div>
    );
  }

  // If no businesses found, show message
  if (!data || data.count === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-[#AEAEB2]" />
          <h2 className="text-2xl font-bold text-[#1D1D1F] mb-2">Sin negocios asignados</h2>
          <p className="text-[#1D1D1F]/60 mb-6">
            Aun no tienes negocios asignados a tu cuenta. Contacta al administrador.
          </p>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 mx-auto px-4 py-2 text-[#1D1D1F]/80 hover:text-[#1D1D1F]"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1D1D1F] mb-2">
              Hola, {data.client_name}
            </h1>
            <p className="text-[#1D1D1F]/60">Selecciona el negocio que deseas ver</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-[#1D1D1F]/60 hover:text-[#1D1D1F] transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Salir
          </button>
        </div>

        {/* Businesses Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <BusinessCards businesses={data.businesses} onSelect={selectBusiness} />
        </div>
      </div>
    </div>
  );
};

// Separate component to avoid .map() in main render
function BusinessCards({ businesses, onSelect }) {
  const cards = [];
  for (let i = 0; i < businesses.length; i++) {
    const biz = businesses[i];
    cards.push(
      <button
        key={biz.business_id}
        onClick={() => onSelect(biz)}
        className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-left hover:bg-white/20 transition-all hover:scale-[1.02] group"
        data-testid={`business-card-${i}`}
      >
        <div className="flex items-start justify-between mb-4">
          {/* Show business logo if available, otherwise default icon */}
          {biz.logo_url ? (
            <div className="w-14 h-14 rounded-lg overflow-hidden bg-white/20 backdrop-blur flex-shrink-0">
              <img 
                src={biz.logo_url} 
                alt={biz.name}
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="p-3 bg-gradient-to-br from-[#e03c3c] to-[#f47c2a] rounded-lg">
              <Briefcase className="w-6 h-6 text-[#1D1D1F]" />
            </div>
          )}
          <ArrowRight className="w-5 h-5 text-[#1D1D1F]/40 group-hover:text-[#1D1D1F] group-hover:translate-x-1 transition-all" />
        </div>
        
        <h3 className="text-xl font-bold text-[#1D1D1F] mb-2">{biz.name}</h3>
        
        <div className="flex items-center gap-3">
          {biz.package_type && (
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              biz.package_type === 'premium' 
                ? 'bg-[rgba(232,106,31,0.15)] text-[#E86A1F]' 
                : 'bg-blue-500/20 text-blue-300'
            }`}>
              {biz.package_type === 'premium' ? 'Premium' : 'Basico'}
            </span>
          )}
          {biz.drive_folder_id && (
            <span className="text-xs text-green-400 flex items-center gap-1">
              <FolderOpen className="w-3 h-3" /> Archivos
            </span>
          )}
        </div>
      </button>
    );
  }
  return <>{cards}</>;
}
